/**
 * Columns Postgres computes for itself.
 *
 * Every one of these is `generated always as (...) stored`. Sending a value
 * for one — even null, even the value the server itself just returned —
 * fails the whole statement with:
 *
 *   cannot insert a non-DEFAULT value into column "downtime_hours"
 *
 * That is easy to trip over: a drawer loads a row from the local mirror,
 * the user edits two fields, and the whole row goes back including the
 * columns the database derived. So the stripping is done centrally rather
 * than remembered in nine places — a new generated column is one line here
 * and every write path is correct.
 *
 * Keep this in step with `generated always as` in supabase/migrations.
 */
export const GENERATED_COLUMNS: Record<string, readonly string[]> = {
  daily_diary: ['engine_hours'],
  diary_activities: ['tonnes_estimated'],
  weighbridge_tickets: ['net_tonnes'],
  blasts: ['tonnes_estimated', 'tonnes_usable'],
  breakdowns: ['downtime_hours'],
  weather_events: ['hours_lost'],
  liner_installations: ['life_hours', 'total_cost'],
  job_card_labour: ['cost'],
  job_card_parts: ['total_cost'],
  fuel_receipts: ['metered_litres', 'tank_gain'],
  plant_production: ['good'],
};

/** A copy of `row` with the columns the database owns removed. */
export function writable<T extends Record<string, unknown>>(table: string, row: T): T {
  const generated = GENERATED_COLUMNS[table];
  if (!generated?.length) return row;

  const out = { ...row };
  for (const column of generated) delete out[column];
  return out;
}
